import { SeaResponseDTO, SeaRouteDetailsDTO } from "../api/response.types";
import {
  ContainerModel,
  EventModel,
  LocationModel,
  SealineNameModel,
  TransformedDemurageModel,
  VesselModel,
} from "./types";
import { SeaTransformedEventModel } from "./types";
import {
  seaErrorMessageMap,
  SeaMessagesModel,
  SeaReferenceTypesModel,
  SeaStatusesModel,
} from "entities/shared/seaCard.types";
import {
  CoordinateModel,
  DateTimeModel,
  SearchReferenceModel,
  ShipmentVariantModel,
} from "entities/shared/shipmentCard.types";
import { RouteDataType, TransportTypes } from "shared/api/lib/map.types";
import { MapRoute, MapRouteModel } from "entities/shared/mapRouteModel";
import { SubscriptionDataModel, SubscriptionModel } from "entities/shared/subscriptionModel";
import { calcProgress } from "entities/shared/utils";
import { BaseModel } from "shared/api/lib/baseModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { isAppIntegration } from "shared/utils/url.utils";

import { SearchApiParser } from "../api/parser";

export const isSeaSearchShipmentModel = (model: Nullable<{ responseType: string }>): model is SeaSearchCardModel =>
  model?.responseType === ShipmentVariantModel.SeaSearch;

export class SeaSearchCardModel extends BaseModel<SearchApiParser, SeaResponseDTO> {
  public readonly responseType: ShipmentVariantModel;
  public readonly requestLimitReachedAppIntegration: boolean = false;
  public readonly requestLimitReachedWebApp: boolean = false;
  private readonly _subscription: SubscriptionModel | null = null;
  private _route: MapRoute | null = null;

  constructor(DTO: SeaResponseDTO) {
    super({ apiParser: new SearchApiParser(DTO), DTO });

    this._subscription = new SubscriptionModel();
    this._route = new MapRoute(this.adaptRouteData());

    this.responseType = ShipmentVariantModel.SeaSearch;
    this.requestLimitReachedAppIntegration = this.requestLimitReached && isAppIntegration;
    this.requestLimitReachedWebApp = this.requestLimitReached && !isAppIntegration;
  }

  get isSuccess(): boolean {
    return this.apiParser.isSuccess;
  }

  get hasData(): boolean {
    return this.apiParser.hasData;
  }

  get requestLimitReached(): boolean {
    return this.apiParser.requestLimitReached;
  }

  get message(): ValueOf<typeof SeaMessagesModel> {
    return this.apiParser.message;
  }

  get reference(): SearchReferenceModel | null {
    return this.apiParser.reference ? this.apiParser.reference.replace(/[^0-9a-zA-Z\-/.]/g, "").toUpperCase() : null;
  }

  get referenceType(): ValueOf<typeof SeaReferenceTypesModel> | null {
    return this.apiParser.referenceType;
  }

  get containersAmount(): number {
    return this.apiParser.containersAmount;
  }

  get status(): ValueOf<typeof SeaStatusesModel> {
    return this.apiParser.status ?? SeaStatusesModel.UNKNOWN;
  }

  get carrierName(): SealineNameModel | null {
    return this.apiParser.carrierName;
  }

  getCarrierScac({ withDefault = false }: { withDefault?: boolean } = {}): string | null {
    return this.apiParser.getCarrierScac({ withDefault });
  }

  get fromDirection(): string | null {
    return this.apiParser.fromDirection;
  }

  get toDirection(): string | null {
    return this.apiParser.toDirection;
  }

  getLocationName(location: Nullable<LocationModel>): string | null {
    const locationDTO = location ? { ...location, country_code: location.countryCode } : null;
    return this.apiParser.getLocationName(locationDTO);
  }

  getLocationById(locationId: Nullable<number>): LocationModel | null {
    const location = this.apiParser.getLocationById(locationId);
    return location ? { ...location, countryCode: location.country_code } : null;
  }

  get polCoordinate(): CoordinateModel | null {
    return this.apiParser.polCoordinate;
  }

  get podCoordinate(): CoordinateModel | null {
    return this.apiParser.podCoordinate;
  }

  get routeData(): SeaRouteDetailsDTO | null {
    return this.apiParser.routeData;
  }

  get currentPin(): CoordinateModel | null {
    return this.apiParser.currentPin;
  }

  get progress(): number {
    return calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta ?? this.predictiveETA,
      seaStatus: this.status,
    });
  }

  get etd(): DateTimeModel | null {
    return this.apiParser.etd();
  }

  get atd(): DateTimeModel | null {
    return this.apiParser.atd();
  }

  get eta(): DateTimeModel | null {
    return this.apiParser.eta();
  }

  get ata(): DateTimeModel | null {
    return this.apiParser.ata();
  }

  get predictiveETA(): DateTimeModel | null {
    return this.apiParser.predictiveEta;
  }

  get containers(): ContainerModel[] {
    const containers = this.apiParser.containers;
    return containers.map((container) => ({
      ...container,
      sizeType: container.size_type,
      isoCode: container.iso_code,
    }));
  }

  get demurage(): TransformedDemurageModel {
    return this.apiParser.demurage;
  }

  get containersSizeTypes(): string[] {
    return this.apiParser.containersSizeTypes;
  }

  get events(): EventModel[] {
    return this.apiParser.events;
  }

  getTransformedEvents(targetEvents: Nullable<EventModel[]>): SeaTransformedEventModel[] {
    return this.apiParser.getTransformedEvents(targetEvents);
  }

  get vessels(): VesselModel[] {
    return this.apiParser.getVessels();
  }

  get searchError(): ValueOf<typeof SeaMessagesModel> {
    return this.apiParser.searchError;
  }

  public searchErrorText(defaultText: string): string | null {
    const searchMessage = this.apiParser.searchError;
    if (searchMessage === SeaMessagesModel.OK) return null;
    return seaErrorMessageMap[searchMessage] ?? defaultText;
  }

  get locations(): LocationModel[] {
    const locations = this.apiParser.locations;
    return locations.map((location) => ({
      ...location,
      countryCode: location.country_code,
    }));
  }

  get subscription(): SubscriptionDataModel | null {
    return this._subscription?.data ?? null;
  }

  get route(): MapRoute | null {
    return this._route;
  }

  private adaptRouteData(): MapRouteModel {
    const legs = [...(this.routeData?.route_legs ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

    return {
      route: legs
        .filter((leg) => leg.route_path?.length)
        .map((leg) => {
          const mode = (leg.transport_mode ?? "VESSEL") as keyof typeof TransportTypes;
          const transportType = TransportTypes[mode] ?? TransportTypes.VESSEL;
          const type =
            transportType === TransportTypes.AIR || transportType === TransportTypes.PLANE
              ? RouteDataType.AIR
              : transportType === TransportTypes.TRUCK || transportType === TransportTypes.RAIL
                ? RouteDataType.LAND
                : RouteDataType.SEA;

          return {
            path: leg.route_path ?? [],
            type,
            transportType,
          };
        }),
      locations: this.locations.map((location) => ({
        id: location.id,
        name: location.name,
        country: location.country,
        countryCode: location.countryCode,
        locode: location.locode,
        lat: location.lat,
        lng: location.lng,
      })),
      ...(this.currentPin && this.status !== SeaStatusesModel.DELIVERED ? { currentPin: this.currentPin } : {}),
    };
  }
}
